import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Modal,
  LinearProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ParkIcon from "@mui/icons-material/Park";
import Co2Icon from "@mui/icons-material/Co2";
import AdjustIcon from "@mui/icons-material/Adjust";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";

import useAppNavigate from "hooks/useAppNavigate";
import { useSearchParams } from "react-router-dom";
import { ROUTE } from "components/base-app/AppRoutes";
import AnimatedCounter from "components/core/AnimatedNumber";
import { formatNok, formatNumber, numberDisplaySx } from "libraries/formatNumber";
import {
  ESGScopeEmissions,
  ESGMetrics,
  ESGSourceContexts,
  convertESGToMetrics,
  extractEmissionsWithGemini,
  prepareEsgExtractPayload,
} from "libraries/esgConverter";
import { isPdfFile } from "libraries/pdfToText";


const SCAN_STEPS = [
  "Analyzing document metadata & structure...",
  "Extracting text from document...",
  "Running AI parsing on document text...",
  "Extracting Scope 1 (Direct Operations) emissions...",
  "Extracting Scope 2 (Energy & Indirect) emissions...",
  "Extracting Scope 3 (Supply Chain & Business) emissions...",
  "Validating and normalizing carbon footprint metrics...",
];

const PAGE = {
  background: "#FAFAFA",
  surface: "#FFFFFF",
  surfaceMuted: "#F3F5F5",
  text: "#1A2222",
  textSecondary: "rgba(26, 34, 34, 0.72)",
  textMuted: "rgba(26, 34, 34, 0.5)",
  border: "rgba(26, 34, 34, 0.12)",
  accent: "#f79c6c",
  accentHover: "#ffcd9b",
  accentSoft: "rgba(247, 156, 108, 0.12)",
  accentBorder: "rgba(247, 156, 108, 0.35)",
  green: "#7CB342",
  greenSoft: "rgba(162, 206, 87, 0.18)",
  greenBorder: "#a2ce57",
};

const RESULT_CARD_SX = {
  p: 4,
  borderRadius: 4,
  backgroundColor: PAGE.surface,
  border: `2px solid ${PAGE.accentBorder}`,
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
  height: "100%",
};

const SCOPE_INPUT_SX = {
  color: PAGE.text,
  backgroundColor: PAGE.surfaceMuted,
  borderRadius: 8,
};

const ESG_DEMO_REPORT_PATH =
  "/reports/sopra-steria-2023-corporate-responsibility-report-en-ecobook.pdf";
const ESG_DEMO_REPORT_NAME =
  "sopra-steria-2023-corporate-responsibility-report-en-ecobook.pdf";
const ESG_DEMO_QUERY_VALUE = "sopra";

interface ESGConverterPageProps {
  embed?: boolean;
}

const ESGConverterPage = ({ embed = false }: ESGConverterPageProps) => {
  const navigateTo = useAppNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const demoAutoStartedRef = useRef(false);

  // App States
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStepIndex, setProcessingStepIndex] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (uploadedPdfUrl) {
        URL.revokeObjectURL(uploadedPdfUrl);
      }
    };
  }, [uploadedPdfUrl]);

  const setUploadedPdfPreview = (file: File) => {
    setUploadedPdfUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return isPdfFile(file) ? URL.createObjectURL(file) : null;
    });
    setIsPdfPreviewOpen(false);
  };

  // ESG Data State
  const [emissions, setEmissions] = useState<ESGScopeEmissions>({
    scope1: 0,
    scope2: 0,
    scope3: 0,
  });

  const [contexts, setContexts] = useState<ESGSourceContexts>({});

  // Calculate Metrics
  const metrics: ESGMetrics = convertESGToMetrics(emissions);

  // Drag Handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processFile = async (file: File) => {
    setFileName(file.name);
    setUploadedPdfPreview(file);
    setIsProcessing(true);
    setProcessingStepIndex(0);
    setShowResults(false);

    try {
      // Simulate steps for UI progression while loading
      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep < SCAN_STEPS.length - 1) {
          currentStep += 1;
          setProcessingStepIndex(currentStep);
        }
      }, 700);

      const extractPayload = await prepareEsgExtractPayload(file);

      const { emissions: newEmissions, contexts: newContexts } =
        await extractEmissionsWithGemini(extractPayload);

      clearInterval(interval);
      setProcessingStepIndex(SCAN_STEPS.length - 1); // final step
      
      setEmissions(newEmissions);
      setContexts(newContexts);
      
      // Short delay before showing results for UI smoothness
      setTimeout(() => {
        setIsProcessing(false);
        setShowResults(true);
      }, 500);

    } catch (error: any) {
      console.error("Error processing file:", error);
      setIsProcessing(false);
      alert("Failed to process file with AI. Error: " + (error?.message || "Unknown error"));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const loadDemoReport = async () => {
    setIsProcessing(true);
    setProcessingStepIndex(0);
    setShowResults(false);

    try {
      const response = await fetch(ESG_DEMO_REPORT_PATH);
      if (!response.ok) {
        throw new Error("Demo report not found");
      }

      const blob = await response.blob();
      const file = new File([blob], ESG_DEMO_REPORT_NAME, {
        type: "application/pdf",
      });
      await processFile(file);
    } catch (error: unknown) {
      console.error("Error loading demo report:", error);
      setIsProcessing(false);
      alert(
        "Could not load the demo report. Error: " +
          (error instanceof Error ? error.message : "Unknown error")
      );
    }
  };

  useEffect(() => {
    if (!embed || demoAutoStartedRef.current) {
      return;
    }

    if (searchParams.get("demo") !== ESG_DEMO_QUERY_VALUE) {
      return;
    }

    demoAutoStartedRef.current = true;
    void loadDemoReport();
  }, [embed, searchParams]);

  // Input Change Handlers
  const handleScopeChange = (scope: keyof ESGScopeEmissions, value: string) => {
    const numericValue = value === "" ? 0 : parseFloat(value);
    setEmissions((prev) => ({
      ...prev,
      [scope]: isNaN(numericValue) ? 0 : numericValue,
    }));
  };

  const handleBack = () => {
    navigateTo(ROUTE.PROFILE_PAGE);
  };

  return (
    <Box
      sx={{
        backgroundColor: PAGE.background,
        minHeight: "100vh",
        color: PAGE.text,
        pb: 8,
      }}
    >
      {!embed && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 2,
            backgroundColor: PAGE.surface,
            borderBottom: `1px solid ${PAGE.border}`,
          }}
        >
          <IconButton onClick={handleBack} sx={{ color: PAGE.accent }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 500, color: PAGE.text }}>
            ESG Impact Converter
          </Typography>
          <Box flexGrow={1} />
          <AutoAwesomeIcon sx={{ color: PAGE.accent, mr: 1 }} />
          <Typography variant="caption" sx={{ color: PAGE.textMuted, fontWeight: 500 }}>
            AI Powered
          </Typography>
        </Box>
      )}

      <Container maxWidth="md" sx={{ mt: embed ? 2 : 4 }}>
        <Stack spacing={4}>
          
          {/* Main Title Section */}
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: PAGE.accent }}>
              ESG Carbon Compensation
            </Typography>
            <Typography variant="body1" sx={{ color: PAGE.textSecondary, maxWidth: "600px", mx: "auto" }}>
              Upload your company's ESG report. Our AI engine extracts Scope 1, 2, and 3 emissions and maps them directly to tree conservation targets in the rainforest.
            </Typography>
          </Box>

          {/* File Upload Area */}
          {!isProcessing && (
            <Paper
              elevation={0}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              sx={{
                p: 5,
                border: `2px dashed ${dragActive ? PAGE.accent : PAGE.border}`,
                borderRadius: 4,
                backgroundColor: dragActive ? PAGE.accentSoft : PAGE.surface,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                textAlign: "center",
                "&:hover": {
                  borderColor: PAGE.accent,
                  backgroundColor: PAGE.accentSoft,
                },
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept=".pdf,.txt,.doc,.docx"
              />
              <UploadFileIcon sx={{ fontSize: 60, color: PAGE.accent, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: PAGE.text }}>
                {fileName ? fileName : "Drag & drop your ESG Report"}
              </Typography>
              <Typography variant="body2" sx={{ color: PAGE.textMuted }}>
                Supports PDF, TXT, and DOCX. PDF text is extracted in your browser before upload for a smaller, faster request.
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                sx={{
                  mt: 3,
                  borderRadius: 3,
                  borderColor: PAGE.accent,
                  color: PAGE.accent,
                  "&:hover": {
                    borderColor: PAGE.accentHover,
                    backgroundColor: PAGE.accentSoft,
                  },
                }}
              >
                Browse Files
              </Button>
            </Paper>
          )}

          {/* AI Loader Scan State */}
          {isProcessing && (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 4,
                backgroundColor: PAGE.surface,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                textAlign: "center",
                border: `1px solid ${PAGE.border}`,
              }}
            >
              <Box sx={{ width: "100%", mb: 3 }}>
                <LinearProgress
                  color="secondary"
                  variant="determinate"
                  value={((processingStepIndex + 1) / SCAN_STEPS.length) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: PAGE.surfaceMuted,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      backgroundImage: "linear-gradient(45deg, #f79c6c 30%, #a2ce57 90%)",
                    },
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, color: PAGE.text }}>
                <AutoAwesomeIcon sx={{ color: PAGE.accent, animation: "spin 2s linear infinite" }} />
                AI Extraction in Progress...
              </Typography>
              <Typography variant="body2" sx={{ color: PAGE.accent, fontWeight: 500, mt: 2, height: 24 }}>
                {SCAN_STEPS[processingStepIndex]}
              </Typography>
            </Paper>
          )}

          {showResults && uploadedPdfUrl && (
            <Box display="flex" justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setIsPdfPreviewOpen(true)}
                sx={{
                  borderRadius: 3,
                  borderColor: PAGE.accent,
                  color: PAGE.accent,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: PAGE.accentHover,
                    backgroundColor: PAGE.accentSoft,
                  },
                }}
              >
                View uploaded PDF
              </Button>
            </Box>
          )}

          {/* Results Editor and Metrics */}
          {showResults && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
                alignItems: "stretch",
              }}
            >
              {/* Left Column: Scope Inputs */}
              <Box sx={{ flex: { xs: "1 1 100%", md: "5 1 0" }, minWidth: 0 }}>
                <Paper elevation={0} sx={RESULT_CARD_SX}>
                  <Stack spacing={3}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: PAGE.accent }}>
                      Detected Emissions (tCO₂e)
                    </Typography>

                    <Stack spacing={2}>
                      <Box sx={{ px: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: PAGE.text, mb: 0.5 }}>
                          Scope 1: Direct Emissions
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE.textMuted, display: "block", mb: 1.5 }}>
                          Company-owned vehicles, combustion boilers, gas usage.
                        </Typography>
                        <TextField
                          type="number"
                          fullWidth
                          value={emissions.scope1 || ""}
                          onChange={(e) => handleScopeChange("scope1", e.target.value)}
                          variant="outlined"
                          size="small"
                          color="secondary"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ "& .MuiTypography-root": { color: PAGE.textMuted } }}>
                                tCO₂e
                              </InputAdornment>
                            ),
                            style: SCOPE_INPUT_SX,
                          }}
                        />
                      </Box>

                      <Box sx={{ px: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: PAGE.text, mb: 0.5 }}>
                          Scope 2: Indirect Emissions
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE.textMuted, display: "block", mb: 1.5 }}>
                          Purchased electricity, facilities heating & cooling.
                        </Typography>
                        <TextField
                          type="number"
                          fullWidth
                          value={emissions.scope2 || ""}
                          onChange={(e) => handleScopeChange("scope2", e.target.value)}
                          variant="outlined"
                          size="small"
                          color="secondary"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ "& .MuiTypography-root": { color: PAGE.textMuted } }}>
                                tCO₂e
                              </InputAdornment>
                            ),
                            style: SCOPE_INPUT_SX,
                          }}
                        />
                      </Box>

                      <Box sx={{ px: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: PAGE.text, mb: 0.5 }}>
                          Scope 3: Value Chain
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE.textMuted, display: "block", mb: 1.5 }}>
                          Suppliers, waste disposal, flights, deliveries, lifecycle.
                        </Typography>
                        <TextField
                          type="number"
                          fullWidth
                          value={emissions.scope3 || ""}
                          onChange={(e) => handleScopeChange("scope3", e.target.value)}
                          variant="outlined"
                          size="small"
                          color="secondary"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ "& .MuiTypography-root": { color: PAGE.textMuted } }}>
                                tCO₂e
                              </InputAdornment>
                            ),
                            style: SCOPE_INPUT_SX,
                          }}
                        />
                      </Box>
                    </Stack>

                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        backgroundColor: PAGE.accentSoft,
                        border: `1px solid ${PAGE.accentBorder}`,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: PAGE.textSecondary, mb: 0.5 }}>
                        Total Carbon Footprint
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: PAGE.accent, ...numberDisplaySx }}>
                        {formatNumber(emissions.scope1 + emissions.scope2 + emissions.scope3)}{" "}
                        <Box component="span" sx={{ fontSize: "1.25rem", fontWeight: 600, color: PAGE.textSecondary }}>
                          tCO₂e
                        </Box>
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>

              {/* Right Column: Compensation Summary Dashboard */}
              <Box sx={{ flex: { xs: "1 1 100%", md: "7 1 0" }, minWidth: 0 }}>
                <Paper elevation={0} sx={RESULT_CARD_SX}>
                  <Stack spacing={3}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: PAGE.accent }}>
                      Rainforest Protection Plan
                    </Typography>

                    {/* Trees Highlight */}
                    <Box display="flex" alignItems="center" gap={2} sx={{ p: 2, borderRadius: 3, backgroundColor: PAGE.greenSoft, border: `1px solid ${PAGE.greenBorder}` }}>
                      <ParkIcon sx={{ fontSize: 48, color: PAGE.greenBorder }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: PAGE.textSecondary, display: "block" }}>
                          Total Rainforest Trees Saved
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: PAGE.green, ...numberDisplaySx }}>
                          <AnimatedCounter value={metrics.savedTrees} />
                        </Typography>
                      </Box>
                    </Box>

                    {/* Metric Cards List */}
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" sx={{ px: 1 }}>
                        <AdjustIcon sx={{ color: PAGE.accent, mr: 2 }} />
                        <Typography sx={{ color: PAGE.textSecondary }}>Saved Area Impact (m²):</Typography>
                        <Box flexGrow={1} />
                        <Typography fontWeight={600} sx={{ color: PAGE.accent, ...numberDisplaySx }}>
                          <AnimatedCounter value={metrics.savedAreaMeters} />
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ backgroundColor: PAGE.border }} />
                      
                      <Box display="flex" alignItems="center" sx={{ px: 1 }}>
                        <SportsSoccerIcon sx={{ color: PAGE.accent, mr: 2 }} />
                        <Typography sx={{ color: PAGE.textSecondary }}>Saved Soccer Fields:</Typography>
                        <Box flexGrow={1} />
                        <Typography fontWeight={600} sx={{ color: PAGE.accent, ...numberDisplaySx }}>
                          <AnimatedCounter value={metrics.savedAreaSoccerFields} />
                        </Typography>
                      </Box>

                      <Divider sx={{ backgroundColor: PAGE.border }} />

                      <Box display="flex" alignItems="center" sx={{ px: 1 }}>
                        <Co2Icon sx={{ color: PAGE.accent, mr: 2 }} />
                        <Typography sx={{ color: PAGE.textSecondary }}>CO₂ Absorbed (metric tons):</Typography>
                        <Box flexGrow={1} />
                        <Typography fontWeight={600} sx={{ color: PAGE.accent, ...numberDisplaySx }}>
                          <AnimatedCounter value={metrics.savedCO2} />
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Compensation Cost Block */}
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        backgroundColor: PAGE.accentSoft,
                        border: `1px solid ${PAGE.accentBorder}`,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: PAGE.textSecondary, mb: 0.5 }}>
                        Required Conservation Funding
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: PAGE.accent, mb: 1, ...numberDisplaySx }}>
                        <AnimatedCounter value={metrics.kronerDonated} format="currency-nok" />
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE.textMuted, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        Calculated at ~9.17 NOK/tree using the 2026 Map Impact formula.
                      </Typography>
                    </Box>

                    {/* Compensate Action */}
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={() => setIsSuccessModalOpen(true)}
                      sx={{
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 600,
                        backgroundColor: PAGE.accent,
                        color: PAGE.text,
                        "&:hover": {
                          backgroundColor: PAGE.accentHover,
                        },
                      }}
                    >
                      Compensate Carbon Footprint
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}

          {/* AI Extraction Reference Sources Panel */}
          {showResults && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                backgroundColor: PAGE.surface,
                border: `1px solid ${PAGE.border}`,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <AutoAwesomeIcon sx={{ color: PAGE.accent, fontSize: 22 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: PAGE.accent }}>
                  AI Extraction Reference Sources
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: PAGE.textSecondary, mb: 3 }}>
                The AI engine extracted and verified the carbon emissions metrics using the following raw reference segments from your report:
              </Typography>
              
              <Stack spacing={2.5}>
                {contexts.scope1 && (
                  <Box sx={{ pl: 2, borderLeft: `3px solid ${PAGE.accent}` }}>
                    <Typography variant="caption" sx={{ color: PAGE.accent, fontWeight: 700, display: "block", letterSpacing: 0.5 }}>
                      SCOPE 1 REFERENCE CLASSIFICATION
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: PAGE.textSecondary, mt: 0.5, lineHeight: 1.6 }}>
                      {contexts.scope1}
                    </Typography>
                  </Box>
                )}
                {contexts.scope2 && (
                  <Box sx={{ pl: 2, borderLeft: `3px solid ${PAGE.accent}` }}>
                    <Typography variant="caption" sx={{ color: PAGE.accent, fontWeight: 700, display: "block", letterSpacing: 0.5 }}>
                      SCOPE 2 REFERENCE CLASSIFICATION
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: PAGE.textSecondary, mt: 0.5, lineHeight: 1.6 }}>
                      {contexts.scope2}
                    </Typography>
                  </Box>
                )}
                {contexts.scope3 && (
                  <Box sx={{ pl: 2, borderLeft: `3px solid ${PAGE.accent}` }}>
                    <Typography variant="caption" sx={{ color: PAGE.accent, fontWeight: 700, display: "block", letterSpacing: 0.5 }}>
                      SCOPE 3 REFERENCE CLASSIFICATION
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: PAGE.textSecondary, mt: 0.5, lineHeight: 1.6 }}>
                      {contexts.scope3}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>

      {/* Success Modal */}
      <Modal
        open={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper
          elevation={5}
          sx={{
            p: 4,
            maxWidth: 450,
            borderRadius: 4,
            backgroundColor: PAGE.surface,
            color: PAGE.text,
            border: `2px solid ${PAGE.greenBorder}`,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            textAlign: "center",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: PAGE.greenBorder, mb: 2 }} />
          <Typography id="success-modal-title" variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1, color: PAGE.text }}>
            Footprint Compensated!
          </Typography>
          <Typography id="success-modal-description" sx={{ color: PAGE.textSecondary, mb: 3 }}>
            Thank you for being a rainforest guardian. Your donation of {formatNok(metrics.kronerDonated)} will save {formatNumber(metrics.savedTrees)} trees in the rainforest, offsetting {formatNumber(metrics.savedCO2)} tons of carbon.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setIsSuccessModalOpen(false)}
            sx={{
              py: 1.2,
              borderRadius: 3,
              fontWeight: 600,
              backgroundColor: PAGE.greenBorder,
              color: PAGE.text,
              "&:hover": {
                backgroundColor: "#c5e1a5",
              },
            }}
          >
            Close
          </Button>
        </Paper>
      </Modal>

      {/* PDF Preview Modal */}
      <Modal
        open={isPdfPreviewOpen}
        onClose={() => setIsPdfPreviewOpen(false)}
        aria-labelledby="pdf-preview-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
        }}
      >
        <Paper
          elevation={5}
          sx={{
            width: "calc(100vw - 24px)",
            maxWidth: "none",
            height: "calc(100vh - 24px)",
            borderRadius: 2,
            backgroundColor: PAGE.surface,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${PAGE.border}`,
            }}
          >
            <Typography id="pdf-preview-title" variant="subtitle1" sx={{ fontWeight: 600, color: PAGE.text }}>
              {fileName}
            </Typography>
            <IconButton onClick={() => setIsPdfPreviewOpen(false)} aria-label="Close PDF preview">
              <CloseIcon />
            </IconButton>
          </Box>
          {uploadedPdfUrl && (
            <Box component="iframe" src={uploadedPdfUrl} title={fileName} sx={{ flex: 1, width: "100%", border: "none" }} />
          )}
        </Paper>
      </Modal>
    </Box>
  );
};

export default ESGConverterPage;
